import { ActionType, ProColumns } from '@ant-design/pro-components'
import { DatePicker, Form, Input, message, Popconfirm, Tooltip } from 'antd'
import FormItem from 'antd/es/form/FormItem'
import { ROUTES } from 'base/routes/routes'
import BoxContainer from 'base/ui/components/BoxContainer'
import CustomButton from 'base/ui/components/Button'
import CustomSelect from 'base/ui/components/CustomSelect'
import CustomProTable, { tablePagination } from 'base/ui/components/CustomTable'
import { Filter, Plus } from 'base/ui/components/Icons'
import dayjs from 'dayjs'
import useDebounce from 'hooks/useDebounce'
import usePartialState from 'hooks/usePartialState'
import { cloneDeep } from 'lodash'
import { observer } from 'mobx-react'
import { getDropdownCatalog } from 'pages/Common/dropdown'
import PageWrapper from 'pages/Components/PageWrapper'
import { SearchBox, SearchForm, SearchItem } from 'pages/Components/SearchBox'
import { SearchModel } from 'pages/model'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { CommonModel } from 'services/Main/common_services'
import { ManageRewardModel } from 'services/Main/exchangeRewards_services'
import { commonPresenter, commonStore, exchangeRewardsPresenter, exchangeRewardsStore } from 'stores/root_store'
import { DATE_FORMAT_POST, PAGE_SIZE_DEFAULT, SYSTEM_TITLE } from 'utils/constants'
import { formatNumber } from 'utils/formatHelper'
import { useCheckPermission } from 'utils/permissionHelper'
import { customFilterOption } from 'utils/searchHelper'
import * as Icon from 'base/ui/components/Icons';
import Button from 'base/ui/components/Button'

const { RangePicker } = DatePicker;

// QUẢN LÝ ĐỔI THƯỞNG
const manageRewardsPage = () => {
  return observer(() => {
    // Kiểm tra phân quyền
    useCheckPermission('INDEX');
    //=====================================CHUYỂN NGÔN NGỮ=====================================//
    const { t } = useTranslation();
    document.title = 'Quản lý đổi thưởng' + ' - ' + SYSTEM_TITLE;

    const [form] = Form.useForm();
    const [open, setOpen] = useState({
      openDrawer: false,
      openModal: false,
      openConfirm: false,
    });

    // data table
    const [dataTable, setDataTable] = useState<ManageRewardModel[]>([]);
    const tableRef = useRef<ActionType>();

    // data common date
    const [listCommonDate, setListCommonDate] = useState<CommonModel[]>([]);
    // disable rangePicker khi chọn ngày từ common
    const [disableRangePicker, setDisableRangePicker] = useState(false);
    const [membershipLevel, setMembershipLevel] = useState<CommonModel[]>([])
    const [programList, setProgramList] = useState<CommonModel[]>([])
    // ---------------XỬ LÝ COLUMN VÀ TABLE-----------------------------------------------------------------------------------------------------
    const columns: ProColumns<ManageRewardModel>[] = [
      // stt
      {
        title: 'STT',
        dataIndex: 'stt',
        key: 'stt',
        fixed: 'left',
        align: 'center',
        width: 70,
      },
      // Mã đổi thưởng
      {
        title: t('RedemptionCode'),
        dataIndex: 'exchangeRewardCode',
        key: 'exchangeRewardCode',
        fixed: 'left',
        align: 'center',
        width: 150,
        render: (_, record) => {
          return <div className='flex items-center justify-center gap-2'>
            <span>{record?.exchangeRewardCode}</span>
            <Popconfirm
              title={t('SyncSap')}
              description={t('MesSyncSap')}
              onConfirm={async () => {
                const result: any = await exchangeRewardsPresenter.syncRewardCode({
                  exchangeRewardCode: record.exchangeRewardCode
                })
                if (result?.isSuccess) {
                  message.success(result?.message)
                }
              }}
              icon={
                <Icon.SynchronizeArrows width="13" height="13" className="!fill-[#1a8ba3]" />
              }
            >
              <Tooltip title={t('SyncSap')}>
                <Button>
                  <Icon.SynchronizeArrows width="13" height="13" className="!fill-[#1a8ba3]" />
                </Button>
              </Tooltip>
            </Popconfirm>
          </div>
        }
      },
      // Date (ngày đổi thưởng)
      {
        title: 'Ngày đổi thưởng',
        dataIndex: 'exchangeRewardDate',
        key: 'exchangeRewardDate',
        width: 150,
        valueType: 'dateTime',
        fieldProps: {
          format: 'DD/MM/YYYY',
        },
        align: 'center',
        fixed: 'left'
      },
      // Mã khách hàng
      {
        title: 'Mã khách hàng',
        dataIndex: 'customerCode',
        key: 'customerCode',
        width: 150,
        align: 'center',
        fixed: 'left'
      },
      // Tên khách hàng
      {
        title: 'Tên khách hàng',
        dataIndex: 'customerName',
        key: 'customerName',
        width: 250,
        ellipsis: true
      },
      // Số điện thoại
      {
        title: 'Số điện thoại',
        dataIndex: 'phone',
        key: 'phone',
        width: 120,
        ellipsis: true
      },
      // Mã chương trình
      {
        title: 'Mã chương trình',
        dataIndex: 'programCode',
        key: 'programCode',
        width: 150,
      },
      // Tên chương trình
      {
        title: 'Tên chương trình',
        dataIndex: 'programName',
        key: 'programName',
        width: 250,
        ellipsis: true
      },
      // Hạng thành viên
      {
        title: 'Hạng thành viên',
        dataIndex: 'membershipRank',
        key: 'membershipRank',
        width: 150,
      },
      // Tổng DS tích lũy
      {
        title: 'Tổng DS tích lũy',
        dataIndex: 'totalRevenue',
        key: 'totalRevenue',
        width: 150,
        align: 'right',
        render: (_, record) => (<>{formatNumber(record.totalRevenue, 0)}</>)
      },
      // ATP
      {
        title: 'ATP',
        dataIndex: 'aptRevenue',
        key: 'aptRevenue',
        width: 160,
        align: 'right',
        render: (_, record) => (<>{formatNumber(record.aptRevenue, 0)}</>)
      },
      // Sử dụng
      {
        title: 'Sử dụng',
        dataIndex: 'usedRevenue',
        key: 'usedRevenue',
        width: 150,
        align: 'right',
        render: (_, record) => (<>{formatNumber(record.usedRevenue, 0)}</>)
      },
      // Còn lại/ATP
      {
        title: 'Còn lại/ATP',
        dataIndex: 'remainValue',
        key: 'remainValue',
        width: 150,
        align: 'right',
        render: (_, record) => (<>{formatNumber(record.remainValue, 0)}</>)
      },
      // Hình thức quy đổi
      {
        title: 'Hình thức quy đổi',
        dataIndex: 'conversionForm',
        key: 'conversionForm',
        width: 150,
      },
      // Giá trị quà tặng
      {
        title: 'Giá trị quà tặng',
        dataIndex: 'giftValue',
        key: 'giftValue',
        width: 150,
        align: 'right',
        render: (_, record) => (<>{formatNumber(record.giftValue, 0)}</>)
      },
      // Đơn vị
      {
        title: 'Đơn vị',
        dataIndex: 'unit',
        key: 'unit',
        width: 100,
      },
      // Chức năng
      // {
      //   title: t('Chức năng'),
      //   dataIndex: 'chucNang',
      //   key: 'chucNang',
      //   width: 100,
      //   render: (_, record) => (
      //     <div className="flex gap-2">
      //       <ButtonDelete />
      //       <ButtonEdit />
      //       {/* <ButtonNonActive /> */}
      //     </div>
      //   ),
      // },
    ];

    //--- search state ------------------------------------------------------------
    const [dataPaging, setDataPaging] = useState<SearchModel>({
      paging: {
        pageSize: PAGE_SIZE_DEFAULT,
        pageIndex: 1,
        orderBy: undefined,
        orderByDesc: undefined,
      },
    });

    const [loading, setLoading] = usePartialState({
      loadingSearch: false,
      loading: false,
      exportBtn: false
    })


    // show filter form
    const showDrawer = () => {
      setOpen({ ...open, openDrawer: true });
    };
    // close filter form
    const onClose = () => {
      setOpen({ ...open, openDrawer: false });
    };

    const getListProgram = async (key?: string) => {
      await commonPresenter.getListProgram(commonStore, key)
      if (commonStore.commonRespone.isSuccess) {
        setProgramList(commonStore.commonRespone.data)
      }
    }
    const debounceListProgram = useDebounce(getListProgram, 500)


    // Lấy danh sách common date
    const getListCommonDate = async () => {
      setLoading({ loadingSearch: true })
      await commonPresenter.getListSelectCommonDate(commonStore);
      if (commonStore.commonRespone.isSuccess) {
        setListCommonDate(commonStore.commonRespone.data);
      }
      setLoading({ loadingSearch: false })
    };
    // Lấy ngày bằng common data
    const getDateByKey = async (key: string) => {
      setLoading({ loadingSearch: true })
      await commonPresenter.getDateByCommonDate(commonStore, key);
      if (commonStore.commonDate.fromDate) {
        setDataPaging({
          ...dataPaging,
          fromTime: commonStore.commonDate.fromDate ? dayjs(commonStore.commonDate.fromDate)?.format(DATE_FORMAT_POST) : undefined,
          toTime: commonStore.commonDate.toDate ? dayjs(commonStore.commonDate.toDate)?.format(DATE_FORMAT_POST) : undefined,
        });

        form.setFieldValue("ngayDoiThuong", [
          dayjs(commonStore.commonDate.fromDate),
          dayjs(commonStore.commonDate.toDate),
        ]);


      } else {
        form.setFieldValue("ngayDoiThuong", undefined);
      }
      setLoading({ loadingSearch: false })
    };

    useEffect(() => {
      getDropdownCatalog({ onSetDropdown: setMembershipLevel, params: { catalogTypeCode: 'MembershipLevel' } })
      getListCommonDate()
      debounceListProgram()
    }, [])



    const getDataTable = async (dataSearch: any) => {
      await exchangeRewardsPresenter.getListManage(exchangeRewardsStore, dataSearch);
      if (exchangeRewardsStore.listManageRes.isSuccess) {
        const dataTemp = cloneDeep(exchangeRewardsStore.listManageRes.data);
        setDataTable(dataTemp);
        return dataTemp;
      }
      setDataTable([]);
      return [];
    };


    return (
      <PageWrapper
        breadcrumb={[
          {
            text: 'Quản lý đổi thưởng'
          },
        ]}
        extras={
          <>
            {/* {checkPermission('CREATE') && (
            <Link to={ROUTES.CAMPAIGN.CONTENT_MANAGE.CREATE.LINK}>
              <CustomButton type="primary" icon={<Plus className="!fill-white" />}>
                {t('btn.create')}
              </CustomButton>
            </Link>
            )} */}
            <Link to={ROUTES.EXCHANGE_REWARDS.MANAGE.CREATE.LINK}>
              <CustomButton type="primary" icon={<Plus className="!fill-white" />}>
                Đổi thưởng
              </CustomButton>
            </Link>
            <CustomButton onClick={showDrawer} type="primary" color="#EFF4F9" className="!px-0 w-8">
              <Filter className="!fill-primary" />
            </CustomButton>
          </>
        }
      >

        {/* Table */}
        <BoxContainer fullHeight>
          <CustomProTable
            bordered
            columns={columns}
            dataSource={dataTable}
            rowKey="stt"
            actionRef={tableRef}
            scroll={{ x: 1700, y: 500 }}
            handleResize={() => { }}
            request={async (params = {}, sort, filter) => {
              const data = await getDataTable({
                ...dataPaging,
                paging: {
                  ...dataPaging.paging,
                  orderBy: Object.values(sort)[0] === 'ascend' ? `${Object.keys(sort)[0].split(',').pop()}` : undefined,
                  orderByDesc:
                    Object.values(sort)[0] === 'descend' ? `${Object.keys(sort)[0].split(',').pop()}` : undefined,
                },
              });

              if (Object.values(sort).length) {
                setDataPaging({
                  ...dataPaging,
                  paging: {
                    ...dataPaging.paging,
                    orderBy:
                      Object.values(sort)[0] === 'ascend' ? `${Object.keys(sort)[0].split(',').pop()}` : undefined,
                    orderByDesc:
                      Object.values(sort)[0] === 'descend' ? `${Object.keys(sort)[0].split(',').pop()}` : undefined,
                  },
                });
              }
              return {
                data: data,
                success: exchangeRewardsStore.listManageRes.isSuccess,
              };
            }}

            toolBarRender={() => [
              // EXPORT =================================================================
              <CustomButton
                loading={loading.exportBtn}
                onClick={async () => {
                  setLoading({ exportBtn: true });
                  const { paging, ...rest } = dataPaging
                  await exchangeRewardsPresenter.exportManageReward(rest)
                  setLoading({ exportBtn: false });
                }}
                icon={<Icon.Download className={'!fill-dark'} />}
              >
                {t('btn.export')}
              </CustomButton>]}

            pagination={{
              ...tablePagination(dataPaging, setDataPaging),
              total: exchangeRewardsStore.listManageRes.paging?.totalCount,
            }}
            options={false}
          />
        </BoxContainer>

        {/* Form search */}
        <SearchBox
          onClose={onClose}
          className="lg:!w-[35%]"
          open={open.openDrawer}
          footer={
            <CustomButton
              htmlType="submit"
              className="my-auto ml-auto"
              type="primary"
              onClick={() => {
                const dataSearch = form.getFieldsValue();
                const dataFilter = Object.fromEntries(Object.entries(dataSearch).filter(([_, value]) => !!value))
                const dateFromTo = form.getFieldValue('ngayDoiThuong');
                setDataPaging({
                  ...dataFilter,
                  fromTime: dateFromTo && dateFromTo[0] ? dayjs(dateFromTo[0])?.format(DATE_FORMAT_POST) : undefined,
                  toTime: dateFromTo && dateFromTo[1] ? dayjs(dateFromTo[1])?.format(DATE_FORMAT_POST) : undefined,
                  paging: { ...dataPaging.paging, pageIndex: 1 },
                });
                form.submit();
                onClose()
              }}
            >
              {t('btn.search')}
            </CustomButton>
          }
        >
          <SearchForm
            column={'col-2'}
            onFinish={() => {
              tableRef.current?.reload();
            }}
            form={form}>

            {/* Mã khách hàng */}
            <SearchItem label="Mã khách hàng" name={'customerCode'}>
              <Input />
            </SearchItem>
            {/* Tên khách hàng */}
            <SearchItem label="Tên khách hàng" name={'customerName'}>
              <Input />
            </SearchItem>
            {/* Mã chương trình */}
            <SearchItem label="Mã chương trình" name={'programCode'}>
              <Input />
            </SearchItem>
            {/* Chương trình */}
            <SearchItem label="Chương trình" name={'programId'} >
              <CustomSelect
                options={programList.map((item) => ({
                  label: `${item.value}`,
                  value: item.key,
                }))}
                onClear={() => debounceListProgram()}
                showSearch
                allowClear
                popupMatchSelectWidth={400}
                onSearch={(value) => value ? debounceListProgram(value) : debounceListProgram()}
                filterOption={false} />
            </SearchItem>
            {/* Hạng thành viên */}
            <SearchItem label="Hạng thành viên" name={'membershipRank'}>
              <CustomSelect
                options={membershipLevel.map((item) => ({
                  label: `${item.key} | ${item.value}`,
                  value: item.key,
                }))}
                showSearch
                allowClear
                filterOption={customFilterOption} />
            </SearchItem>
            {/* Số điện thoại */}
            <SearchItem label="Số điện thoại" name={'phone'}>
              <Input />
            </SearchItem>

            {/* Ngày đổi thưởng  */}
            <SearchItem name={"commonDate"}
              // rules={[{ required: true, message: "Vui lòng chọn ngày" }]}
              label={"Ngày đổi thưởng"} start col2>
              <CustomSelect
                options={listCommonDate}
                fieldNames={{ label: "value", value: "key" }}
                onChange={async (value, option) => {
                  if (option) {
                    setDisableRangePicker(
                      option.key === "Custom" ? false : true
                    );
                    await getDateByKey(option.key);
                  } else {
                    setDisableRangePicker(false);
                    form.setFieldValue("ngayDoiThuong", undefined);
                  }
                }}
              />
            </SearchItem>
            {/* chọn ngày*/}
            <SearchItem name={"ngayDoiThuong"} col2>
              <RangePicker
                disabled={disableRangePicker}
                format="DD/MM/YYYY"
                className="w-full"
              />
            </SearchItem>

          </SearchForm>
        </SearchBox>


      </PageWrapper>

    )
  })
}

export default manageRewardsPage